import React, { createContext, useContext, useRef } from 'react';

type Callback = () => void;

/**
 * Context type for case view modal
 */
export interface CaseViewModalContextType {
  registerOnModalClose: (cb: Callback) => void;
  triggerModalClose: () => void;
}

/**
 * Context used to share modal-close handler and trigger in the child components.
 */
export const CaseViewModalContext = createContext<CaseViewModalContextType
 | undefined>(undefined);

/**
 * Provides case-view modal close handlers to child components.
 * Stores a single close callback reference and exposes:
 * - registerOnModalClose: sets the callback to run on close
 * - triggerModalClose: executes the registered callback if it exists
 */
export const CaseViewModalProvider = ({ children }: { children: React.ReactNode }) => {
  const modalCloseCallback = useRef<Callback | null>(null);

  const registerOnModalClose = (cb: Callback) => {
    modalCloseCallback.current = cb;
  };

  const triggerModalClose = () => {
    modalCloseCallback.current?.();
  };

  return (
    <CaseViewModalContext.Provider value={{ registerOnModalClose, triggerModalClose }}>
      {children}
    </CaseViewModalContext.Provider>
  );
};

/**
 * Accesses the modal context
 * Must be called within the CaseViewModalProvider.
 */
export const useCaseViewModal = () => {
  const context = useContext(CaseViewModalContext);
  if (!context) {
    throw new Error('invalid use of case view modal context');
  }

  return context;
};
